// CategoryModal.tsx
//
// Cadastro de CATEGORIA DE PRODUTO (Suprimentos). Categoria é como o cardápio
// se organiza: Pizzas, Bebidas, Sobremesas.
//
// O QUE ESTA TELA NÃO É: ela não muda preço, e não "aplica em N de M lojas".
// No fluxo real, a categoria nasce no OPS e desce para a Central como CARGA —
// a DynamicTable lê a lista de `decryptedInfoToken.companyInfo` (case 2), que é
// o retrato do que veio. Editar preço continua sendo assunto do OPS. Aqui se
// cadastra e se corrige o CADASTRO da categoria; prometer propagação de preço
// seria prometer o que esta tela não faz.
//
// ONDE GRAVA: `POST/PATCH suprimentos/categorias`, a tabela
// `asup."Categoria"`. Quem monta a requisição é o `handleSave` da Configuração
// — por isso o payload daqui usa OS NOMES DE COLUNA do agregado (`company`,
// `nome`, `descricao`, `status`) e não os nomes do InfoToken: o servidor
// descarta em silêncio toda chave fora da lista branca do agregado.
//
// A LISTA continua vindo do InfoToken (`companyInfo`, DynamicTable case 2), que
// é o retrato da carga do OPS. Categoria criada aqui grava no banco e não
// aparece na tabela enquanto a lista não passar a ler o REST paginado.
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/_casca";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

interface ModalPadraoProps {
  selectedObject: any;
  handleCloseModal: () => void;
  handleSave: (objectType: string, payload?: any) => any;
  parseInfoValue: (infoValue: string) => any;
  decryptedInfoToken?: any;
}

const CategoryModal: React.FC<ModalPadraoProps> = ({
  selectedObject,
  handleCloseModal,
  handleSave,
  parseInfoValue,
  decryptedInfoToken,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-categorymodal",
  });

  // `{}` é truthy (useModal faz `setSelectedObject(object || {})`): quem diz se
  // é edição é o uuid. `categoriaUuid` é a chave do agregado no REST e é a
  // MESMA que o `handleSave` do shell lê para escolher entre POST e PATCH —
  // usar outra aqui faria o modal dizer "Editar" enquanto o shell criava linha
  // nova. `productCategoryUUID` é o nome montado pela DynamicTable;
  // `companyInfoUuid` é o do registro cru do token.
  const uuidEmEdicao =
    selectedObject?.categoriaUuid ||
    selectedObject?.productCategoryUUID ||
    selectedObject?.productCategoryUuid ||
    selectedObject?.companyInfoUuid ||
    "";
  const ehEdicao = Boolean(uuidEmEdicao);
  const tipoDoModal = ehEdicao ? "updateCategory" : "createCategory";

  // Os campos têm o nome DA COLUNA do agregado `categorias`. `status` é a
  // coluna BOOLEAN de ativo/inativo da tabela — não existe coluna `ativo` lá.
  const [form, setForm] = useState({
    nome: "",
    descricao: "",
    company: "",
    status: true,
  });
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);

  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  /** As empresas do token — categoria é cadastro de EMPRESA, não de loja. */
  const empresas: any[] = useMemo(
    () => decryptedInfoToken?.organization?.companies ?? [],
    [decryptedInfoToken]
  );

  useEffect(() => {
    // Duas origens: a linha já achatada pela DynamicTable, ou o registro cru do
    // token com o JSON ainda em texto em `companyInfoValue` — aí quem abre é o
    // `parseInfoValue` do shell.
    const cru = selectedObject?.companyInfoValue;
    const valores =
      cru && typeof parseInfoValue === "function"
        ? { ...selectedObject, ...parseInfoValue(cru) }
        : selectedObject ?? {};

    setForm({
      nome:
        valores.nome ||
        valores.productCategoryName ||
        valores.companyInfoDescription ||
        "",
      descricao: valores.descricao || valores.productCategoryDescription || "",
      company: valores.company || valores.companyUuid || "",
      status:
        (valores.status ?? valores.ativo ?? valores.companyInfoStatus) !==
        false,
    });
    setAviso(null);
  }, [selectedObject, parseInfoValue]);

  const handleLocalChange = (campo: string, valor: any) => {
    setForm((anterior) => ({ ...anterior, [campo]: valor }));
  };

  const validar = (): string | null => {
    if (!form.nome.trim()) return "O nome da categoria é obrigatório.";
    return null;
  };

  const handleSubmit = async () => {
    const erro = validar();
    if (erro) {
      setAviso({ tom: "erro", texto: erro });
      return;
    }

    setSalvando(true);
    try {
      const r = await handleSave(tipoDoModal, {
        ...form,
        nome: form.nome.trim(),
        descricao: form.descricao.trim(),
        categoriaUuid: uuidEmEdicao || undefined,
      });
      if (r && !r.ok && montado.current) {
        setAviso({ tom: r.tom || "erro", texto: r.texto });
      }
    } finally {
      if (montado.current) setSalvando(false);
    }
  };

  return ReactDOM.createPortal(
    <AlfredoStyles.ModalOverlay>
      <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo}>
        <AlfredoStyles.ModalHeader>
          <AlfredoStyles.ModalTitle id="titulo-categorymodal">
            <AlfredoStyles.MaterialIcons>category</AlfredoStyles.MaterialIcons>
            {ehEdicao ? "Editar Categoria" : "Nova Categoria"}
          </AlfredoStyles.ModalTitle>
          <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.ModalCloseButton>
        </AlfredoStyles.ModalHeader>

        <AlfredoStyles.ModalBody>
          <AlfredoStyles.FormContainer>
            {/* O aviso vem ANTES dos campos de propósito: quem abriu esperando
                mexer em preço precisa descobrir isso antes de digitar. */}
            <AlfredoStyles.AlertMessage $tom="atencao">
              As categorias são <strong>carga do OPS para a Central</strong>: a
              lista que aparece aqui é o retrato do que veio de lá. A edição de{" "}
              <strong>preço continua no OPS</strong>. Esta tela cadastra e
              corrige o cadastro da categoria — ela não aplica preço em loja
              nenhuma e não tem como dizer "aplicado em N de M lojas".
            </AlfredoStyles.AlertMessage>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Dados da categoria
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Nome *</AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    type="text"
                    value={form.nome}
                    onChange={(e) =>
                      handleLocalChange("nome", e.target.value)
                    }
                    placeholder="Ex.: Pizzas, Bebidas, Sobremesas"
                  />
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Empresa</AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={form.company}
                    onChange={(e) =>
                      handleLocalChange("company", e.target.value)
                    }
                  >
                    <option value="">Selecione a empresa</option>
                    {empresas.map((empresa: any) => (
                      <option
                        key={empresa.companyUuid}
                        value={empresa.companyUuid}
                      >
                        {empresa.companyName}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer fullWidth>
                  <AlfredoStyles.Label>Descrição</AlfredoStyles.Label>
                  <AlfredoStyles.TextArea
                    value={form.descricao}
                    onChange={(e) =>
                      handleLocalChange("descricao", e.target.value)
                    }
                    placeholder="O que entra nesta categoria"
                  />
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input
                    type="checkbox"
                    checked={form.status}
                    onChange={(e) =>
                      handleLocalChange("status", e.target.checked)
                    }
                  />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel>
                  Categoria ativa
                  <span className="tooltip">
                    Desmarque para tirar a categoria de uso sem apagá-la
                  </span>
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.AlertMessage $tom="atencao">
              O que você salvar aqui grava em{" "}
              <strong>suprimentos/categorias</strong>, mas a tabela acima ainda
              lê a lista do InfoToken (<code>companyInfo</code>), que é o
              retrato da carga do OPS. Ou seja: a categoria é criada no banco e{" "}
              <strong>não aparece na lista</strong> até esta tela passar a ler o
              REST paginado. Não é o salvamento que falhou.
            </AlfredoStyles.AlertMessage>

            {aviso && (
              <AlfredoStyles.AlertMessage $tom={aviso.tom}>
                {aviso.texto}
              </AlfredoStyles.AlertMessage>
            )}
          </AlfredoStyles.FormContainer>
        </AlfredoStyles.ModalBody>

        <AlfredoStyles.ModalFooter>
          <AlfredoStyles.ButtonGroup>
            <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              Cancelar
            </AlfredoStyles.SecondaryButton>
            <AlfredoStyles.PrimaryButton
              onClick={handleSubmit}
              disabled={salvando}
            >
              <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
              {salvando
                ? "Salvando..."
                : ehEdicao
                ? "Atualizar categoria"
                : "Criar categoria"}
            </AlfredoStyles.PrimaryButton>
          </AlfredoStyles.ButtonGroup>
        </AlfredoStyles.ModalFooter>
      </AlfredoStyles.ModalContent>
    </AlfredoStyles.ModalOverlay>,
    document.body
  );
};

export default CategoryModal;
